import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  type NCRColumnConfig,
  loadNCRColumns,
  renderNCRCell,
} from '../components/quality/qualityColumnConfig';
import { QualityColumnsDropdown } from '../components/quality/QualityColumnsDropdown';
import { useNavigate, Link } from 'react-router-dom';
import { useView } from '../contexts/ViewContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Loader2,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  FileSpreadsheet,
  Filter,
  ChevronDown,
  ChevronUp,
  Check,
} from 'lucide-react';
import {
  qualityAPI,
  platformSettingsAPI,
  NonConformanceReport,
  QualityTabsVisibility,
} from '../lib/api';
import { cn } from '../lib/utils';
import { Pagination } from '../components/ui/pagination';
import { usePagination } from '../hooks';
import { getErrorMessage } from '../hooks/useFetchData';
import { useFormatters } from '../contexts/PreferencesContext';
import { SectionTourTrigger } from '../components/tour/SectionTourTrigger';
import { useIsSupplier } from '../hooks/usePortalMode';
import { toast } from 'sonner';

export default function Quality() {
  const navigate = useNavigate();
  const isSupplier = useIsSupplier();
  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { formatDate, formatNumber } = useFormatters();
  const [columns, setColumns] = useState<NCRColumnConfig[]>(loadNCRColumns);
  const visibleCols = useMemo(() => columns.filter((c) => c.visible), [columns]);
  const [ncrs, setNcrs] = useState<NonConformanceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('ncr_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // NCR Template preference (1 = Mandatory 8D, 2 = Optional 8D)
  const [ncrTemplate, setNcrTemplate] = useState<1 | 2>(1);

  // Quality tab visibility from platform settings
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  const pagination = usePagination({
    pageSize: 15,
    onPageChange: () => {}, // Fetch is triggered by useEffect
  });

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      pagination.reset();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Map frontend display fields to backend ordering fields
  const fieldMapping: Record<string, string> = {
    id: 'id',
    company_name: 'company__name',
    ncr_number: 'ncr_number',
    customer_name: 'customer__name',
    part_number: 'part_number',
    grn_number: 'grn_number',
    ncr_date: 'ncr_date',
    status: 'status',
  };

  type SortableField = keyof typeof fieldMapping;

  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    pagination.reset();
  };

  const getSortIcon = (field: SortableField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const backendField = fieldMapping[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;
      const viewParams = getViewQueryParams();
      const ncrsResponse = await qualityAPI.getNCRs({
        ordering,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        search: debouncedSearchTerm || undefined,
        status: statusFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        ...viewParams,
      });

      setNcrs(ncrsResponse.data.results);
      pagination.setFromResponse(ncrsResponse.data.count);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load quality data'));
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, debouncedSearchTerm, sortField, sortDirection, statusFilter, dateFrom, dateTo, isSupplier, viewKey, getViewQueryParams]);

  useEffect(() => {
    if (viewContextLoading) return;
    fetchData();
  }, [fetchData, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Fetch platform settings for NCR template preference and tab visibility
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        // Fetch display settings for tab visibility
        const displayResponse = await platformSettingsAPI.getDisplaySettings();
        if (displayResponse.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...displayResponse.data.quality_tabs }));
        }

        // Fetch full settings for NCR template
        const response = await platformSettingsAPI.getSettings();
        const template = response.data.ncr_template;
        if (template === 1 || template === 2) {
          setNcrTemplate(template);
        }
      } catch (error) {
        console.error('Error fetching platform settings:', error);
      } finally {
        setTabSettingsLoaded(true);
      }
    };
    fetchPlatformSettings();
  }, []);

  // Redirect if NCRs tab is disabled - navigate to first available quality tab
  useEffect(() => {
    if (!tabSettingsLoaded) return;

    if (!tabVisibility.ncrs) {
      // NCRs disabled - redirect to first available tab
      if (tabVisibility.concessions) {
        navigate('/concessions', { replace: true });
      }
      // If no tabs are available, stay on page but content won't show
    }
  }, [tabSettingsLoaded, tabVisibility, navigate, isSupplier]);

  const handleViewNCR = (ncrId: number) => {
    navigate(`/quality/ncr/${ncrId}/edit`);
  };

  const handleAddNCR = () => {
    navigate('/quality/ncr/new');
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);

      // Fetch all NCRs (no pagination) for export
      const backendField = fieldMapping[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;

      const viewParams = getViewQueryParams();
      const response = await qualityAPI.getNCRs({
        ordering,
        page_size: 1000, // Get all NCRs for export
        search: debouncedSearchTerm || undefined,
        status: statusFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        ...viewParams,
      });

      const exportData = response.data.results;

      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Create CSV content
      const headers = [
        'NCR ID',
        'Customer',
        'Company NCR/SRN No.',
        'Supplier',
        'Part No.',
        'GRN No.',
        'PO No.',
        'Raised By',
        'NCR Date',
        'Status',
        'Severity',
        'Defect Description',
        'Batch Qty',
        'Reject Qty',
        'Shortage Qty',
      ];

      const csvRows = [headers.join(',')];

      for (const ncr of exportData) {
        const row = [
          ncr.id,
          `"${(ncr.supplier_name || ncr.company_name || '').replace(/"/g, '""')}"`,
          `"${(ncr.ncr_number || '').replace(/"/g, '""')}"`,
          `"${(ncr.customer_name || '').replace(/"/g, '""')}"`,
          `"${(ncr.part_number || '').replace(/"/g, '""')}"`,
          `"${(ncr.grn_number || '').replace(/"/g, '""')}"`,
          `"${(ncr.po_number || '').replace(/"/g, '""')}"`,
          `"${(ncr.raised_by_name || '').replace(/"/g, '""')}"`,
          ncr.ncr_date ? formatDate(ncr.ncr_date) : '',
          ncr.status_display || ncr.status,
          ncr.severity || '',
          `"${(ncr.defect_description || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
          ncr.batch_qty || 0,
          ncr.reject_qty || 0,
          ncr.shortage_qty || 0,
        ];
        csvRows.push(row.join(','));
      }

      const csvContent = csvRows.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `NCR_Export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} NCRs to CSV`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export NCRs');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    pagination.reset();
  };

  const getStatusBadge = (status: string, statusDisplay: string) => {
    // Match old CustomerPortal styling
    let badgeClass = '';

    if (status === 'closed') {
      badgeClass = 'bg-[#d4edda] text-[#155724] border-[#28a745]';
    } else if (status === 'deleted') {
      badgeClass = 'bg-[#f8d7da] text-[#721c24] border-[#dc3545]';
    } else {
      // Open statuses (draft, ca_submitted, car_submitted)
      badgeClass = 'bg-[#e9ecef] text-[#495057] border-[#6c757d]';
    }

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${badgeClass}`}>
        {statusDisplay}
      </span>
    );
  };

  const getNextAction = (ncr: NonConformanceReport) => {
    if (ncr.status === 'draft' || ncr.status === 'ca_submitted') {
      return 'Awaiting Supplier';
    } else if (ncr.status === 'car_submitted') {
      return 'Awaiting Customer';
    } else if (ncr.status === 'closed') {
      return 'Closed';
    } else if (ncr.status === 'deleted') {
      return 'Deleted';
    }
    return '-';
  };

  // Get step number based on NCR status
  const getStepFromStatus = (status: string): number => {
    switch (status) {
      case 'draft': return 1;
      case 'ca_submitted': return 2;
      case 'car_submitted': return 3;
      case 'closed': return 4;
      default: return 1;
    }
  };

  // Mini progress indicator - Template 1 (numbered steps)
  const MiniProgressTemplate1 = ({ status }: { status: string }) => {
    const currentStep = getStepFromStatus(status);
    const steps = [
      { num: 1, label: 'Created' },
      { num: 2, label: 'CA' },
      { num: 3, label: 'CAR' },
      { num: 4, label: 'Close' },
    ];

    return (
      <div className="flex items-center gap-0.5">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step.num;
          const isCurrent = currentStep === step.num;
          return (
            <div key={step.num} className="flex items-center">
              <div
                className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold',
                  isCompleted
                    ? 'bg-green-600 text-white'
                    : isCurrent
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                )}
                title={step.label}
              >
                {isCompleted ? <Check className="w-3 h-3" /> : step.num}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'w-3 h-0.5',
                    isCompleted ? 'bg-green-600' : 'bg-gray-200'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Mini progress indicator - Template 2 (emoji steps)
  const MiniProgressTemplate2 = ({ status, ncr }: { status: string; ncr: NonConformanceReport }) => {
    const currentStep = getStepFromStatus(status);
    const liabilityType = ncr.liability_type ?? 0;
    const liabilityDetermined = liabilityType > 0;
    const steps = [
      { icon: '📋', label: 'Created', active: true },
      { icon: liabilityType === 1 ? '🏭' : liabilityType === 2 ? '🏢' : '⚖️', label: 'Liability', active: liabilityDetermined },
      { icon: '⚠️', label: '8D', active: true },
      ...(ncr.is_collection_required ? [{ icon: '📦', label: 'Collect', active: true }] : []),
      ...(ncr.is_credit_required ? [{ icon: '💰', label: 'Credit', active: true }] : []),
      { icon: '✅', label: 'Closed', active: true },
    ];

    return (
      <div className="flex items-center gap-1">
        {steps.map((step, index) => {
          const isCompleted = currentStep > index + 1;
          const isCurrent = currentStep === index + 1;
          return (
            <div key={index} className="flex items-center">
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-sm',
                  isCompleted || isCurrent
                    ? 'bg-green-100'
                    : 'bg-gray-100 opacity-50'
                )}
                title={step.label}
              >
                {step.icon}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'w-2 h-0.5 mx-0.5',
                    isCompleted ? 'bg-green-400' : 'bg-gray-200'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render the appropriate mini progress based on template
  const renderMiniProgress = (ncr: NonConformanceReport) => {
    if (ncr.status === 'deleted') {
      return <span className="text-gray-400 text-xs">Deleted</span>;
    }
    return ncrTemplate === 1
      ? <MiniProgressTemplate1 status={ncr.status} />
      : <MiniProgressTemplate2 status={ncr.status} ncr={ncr} />;
  };

  // Show loading while checking tab settings
  if (!tabSettingsLoaded || (loading && !ncrs.length)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Section Tour Trigger */}
      <div className="flex justify-end mb-4">
        <SectionTourTrigger
          pageKey="quality"
          sectionKey="ncrs"
          variant="link"
          label="Tour this page"
        />
      </div>

      {/* Main Card Container - matches old CustomerPortal layout */}
      <Card className="rounded-lg shadow-sm" data-tour="ncr-table">
        {/* Navigation Tabs Header */}
        <CardHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-2">
              {tabVisibility.ncrs && (
                <Link
                  to="/quality"
                  className="px-4 py-2 text-sm font-medium rounded-md border-2 border-primary bg-primary text-primary-foreground shadow-sm"
                >
                  NCRs
                </Link>
              )}
              {tabVisibility.concessions && (
                <Link
                  to="/concessions"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  Production Permit / Concession
                </Link>
              )}
            </nav>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Toolbar Section */}
          <div className="border-b bg-white px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left side - Add button (supplier-only; customers cannot raise NCRs) */}
              <div>
                {isSupplier && (
                  <Button
                    onClick={handleAddNCR}
                    className="bg-[#1bc5bd] hover:bg-[#0bb4ac] text-white font-semibold uppercase text-sm px-6 py-2"
                    data-tour="add-ncr-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    ADD
                  </Button>
                )}
              </div>

              {/* Right side - Export and Filter buttons */}
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  onClick={handleExportExcel}
                  className="bg-primary hover:bg-primary/90"
                  disabled={exporting}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  {exporting ? 'Exporting...' : 'Export to Excel'}
                </Button>
                <Button
                  variant="default"
                  onClick={() => setShowFilters(!showFilters)}
                  className="bg-primary hover:bg-primary/90"
                  data-tour="ncr-filters"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                  {showFilters ? (
                    <ChevronUp className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-2" />
                  )}
                </Button>
                <QualityColumnsDropdown columns={columns} onChange={setColumns} />
              </div>
            </div>
          </div>

          {/* Collapsible Filter Panel */}
          {showFilters && (
            <div className="border-b bg-gray-50 px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search NCRs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      pagination.reset();
                    }}
                    className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="ca_submitted">CA Submitted</option>
                    <option value="car_submitted">CAR Submitted</option>
                    <option value="closed">Closed</option>
                    <option value="deleted">Deleted</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Date
                  </label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      pagination.reset();
                    }}
                    className="bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Date
                  </label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      pagination.reset();
                    }}
                    className="bg-white"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="text-sm"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          )}

          {/* Data Table Section */}
          <div className="p-6">
            {ncrs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || statusFilter ? 'No NCRs found matching your criteria' : 'No NCRs found'}
              </div>
            ) : (
              <>
                {/* Top Pagination */}
                <div className="mb-4">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={pagination.goToPage}
                    itemLabel="NCRs"
                  />
                </div>

                {/* Table — columns + order driven by qualityColumnConfig (CR-DE-Quality-Cols) */}
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#f3f6f9] border-b">
                        {visibleCols.map((col) => {
                          const sortKey = col.sortField || col.id;
                          return (
                            <th
                              key={col.id}
                              style={{ minWidth: col.minWidth }}
                              className={`text-left py-3 px-4 font-semibold text-sm text-[#3f4254] select-none ${
                                col.sortable ? 'cursor-pointer hover:bg-[#ebedf3]' : ''
                              }`}
                              onClick={col.sortable ? () => handleSort(sortKey) : undefined}
                            >
                              <div className="flex items-center">
                                {col.label}
                                {col.sortable && getSortIcon(sortKey)}
                              </div>
                            </th>
                          );
                        })}
                        <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254] min-w-[160px]">
                          Progress
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254] min-w-[140px]">
                          Next Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ncrs.map((ncr, index) => (
                        <tr
                          key={ncr.id}
                          className={`border-b cursor-pointer transition-colors hover:bg-[#f3f6f9] ${
                            index % 2 === 0 ? 'bg-white' : 'bg-[#f9fafb]'
                          }`}
                          style={{ height: '58px' }}
                          onClick={() => handleViewNCR(ncr.id)}
                        >
                          {visibleCols.map((col) => (
                            <td
                              key={col.id}
                              className={`py-3 px-4 text-sm text-[#3f4254] ${
                                col.id === 'part_number' ? 'font-mono' : ''
                              }`}
                            >
                              {col.id === 'status_display'
                                ? getStatusBadge(ncr.status, ncr.status_display)
                                : renderNCRCell(col, ncr, { formatDate, formatNumber })}
                            </td>
                          ))}
                          <td className="py-3 px-4">{renderMiniProgress(ncr)}</td>
                          <td className="py-3 px-4 text-sm text-[#3f4254]">{getNextAction(ncr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Bottom Pagination */}
                <div className="mt-4">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={pagination.goToPage}
                    itemLabel="NCRs"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
